import { ValidationResult } from 'types/validation';
import { PortfolioTypeFormData } from './steps/PortfolioTypeStep';
import { getMarketModelForwardCurveIds, validatePortfolioNameExists } from 'services/portfolios';
import { PortfolioBasicsFormData } from './steps/PortfolioBasicsStep';
import { validationResultUtils } from 'utils/validation';
import {
    validateForwardCurveConstraints,
    validateGlobalShapes,
    validateHydro,
    validateMarketModelRequiredForwards,
} from 'services/portfolios/service';
import mappedItemTypes from 'services/items/mappedItemTypes';
import { PortfolioItemExtended } from 'types/portfolios';
import { PCW_STEPS } from './constants';

export const validateForwardCurveConstraintsStep = async (
    values: { selected: PortfolioItemExtended[] },
    formData: any
): Promise<ValidationResult | null> => {
    const forwardCurveIds =
        formData?.[PCW_STEPS.PCW_FORWARD_CURVES_STEP_ID]?.selected?.map((item: PortfolioItemExtended) =>
            Number(item.id)
        ) || [];
    const forwardCurveConstraintIds =
        values.selected
            ?.filter((item) => item.typeId === mappedItemTypes.FORWARD_CURVE_CONSTRAINTS.id)
            .map((item) => Number(item.id)) || [];
    const result = await validateForwardCurveConstraints({ forwardCurveConstraintIds, forwardCurveIds });
    return result;
};

export const validateHydroStep = async (values: {
    selected?: PortfolioItemExtended[];
}): Promise<ValidationResult | null> => {
    const hydroIds =
        values.selected?.filter((item) => item.typeId === mappedItemTypes.HYDRO.id).map((item) => Number(item.id)) ||
        [];
    const hydroProjectIds =
        values.selected
            ?.filter((item) => item.typeId === mappedItemTypes.HYDRO_PROJECT.id)
            .map((item) => Number(item.id)) || [];
    const result = await validateHydro({ hydroIds, hydroProjectIds });
    return result;
};

export const validatePortfolioType = async (values: PortfolioTypeFormData): Promise<ValidationResult | null> => {
    if (values.portfolioType === 'econ-dispatch') {
        return null;
    }
    return {
        globalIssues: [
            {
                severity: 'Error',
                message: 'Portfolio type is not supported for this wizard.',
            },
        ],
        issues: [
            {
                index: 0,
                id: 'validation-issues',
                fields: [
                    {
                        name: 'portfolioType',
                        value: null,
                        validationMessages: [
                            {
                                severity: 'Error',
                                message: `Invalid portfolio type`,
                            },
                        ],
                    },
                ],
                generalValidationMessages: [],
            },
        ],
    };
};

export const validatePortfolioBasics = async (values: PortfolioBasicsFormData): Promise<ValidationResult | null> => {
    const result = validationResultUtils.createEmpty();

    // check if portfolio name exists
    const portfolioNameExists = await validatePortfolioNameExists(values.portfolioName);
    if (portfolioNameExists) {
        result.issues[0].fields.push({
            name: 'portfolioName',
            value: null,
            validationMessages: [
                {
                    severity: 'Error',
                    message: `Portfolio "${values.portfolioName}" already exists.`,
                },
            ],
        });
    }

    if (!values.bookId) {
        result.issues[0].fields.push({
            name: 'bookId',
            value: null,
            validationMessages: [
                {
                    severity: 'Error',
                    message: 'Field validation message: Book ID is required.',
                },
            ],
        });
    }

    if (!values.mainMarketModel) {
        result.issues[0].fields.push({
            name: 'mainMarketModel',
            value: null,
            validationMessages: [
                {
                    severity: 'Error',
                    message: 'Field validation message: Main Market Model is required.',
                },
            ],
        });
    }

    // check for valid market model
    try {
        await getMarketModelForwardCurveIds(values.mainMarketModel);
    } catch (error) {
        result.issues[0].fields.push({
            name: 'mainMarketModel',
            value: null,
            validationMessages: [
                {
                    severity: 'Error',
                    message: 'Main market model is not valid, select another one.',
                },
            ],
        });
    }

    const marketModelForwardsValidation = await validateMarketModelRequiredForwards(Number(values.mainMarketModel));

    for (const issue of marketModelForwardsValidation?.issues || []) {
        result.issues.push(issue);
    }

    for (const globalIssue of marketModelForwardsValidation?.globalIssues || []) {
        result.globalIssues.push(globalIssue);
    }

    return validationResultUtils.isEmpty(result) ? null : result;
};

export const validateGlobalShapesStep = async (
    values: { selected: PortfolioItemExtended[] },
    formData: any
): Promise<ValidationResult | null> => {
    const mainMarketModelId = formData?.[PCW_STEPS.PCW_PORTFOLIO_BASICS_STEP_ID]?.mainMarketModel;
    const result = await validateGlobalShapes(mainMarketModelId, { itemIds: values.selected.map((item) => item.id) });
    return result;
};
